import { useState, useCallback, useRef, useEffect } from "react";

interface UseVoiceInputOptions {
  onResult?: (transcript: string) => void;
  onInterimResult?: (transcript: string) => void;
  onError?: (error: string) => void;
  language?: string;
  continuous?: boolean;
}

interface UseVoiceInputReturn {
  isListening: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  startListening: () => void;
  stopListening: () => void;
  clearTranscript: () => void;
}

// Web Speech API interface extensions
interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  readonly isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechGrammarList {
  readonly length: number;
  item(index: number): object;
  [index: number]: object;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  grammars: SpeechGrammarList;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: (this: SpeechRecognition, ev: Event) => any;
  onaudiostart: (this: SpeechRecognition, ev: Event) => any;
  onend: (this: SpeechRecognition, ev: Event) => any;
  onerror: (this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any;
  onnomatch: (this: SpeechRecognition, ev: SpeechRecognitionEvent) => any;
  onresult: (this: SpeechRecognition, ev: SpeechRecognitionEvent) => any;
  onsoundend: (this: SpeechRecognition, ev: Event) => any;
  onsoundstart: (this: SpeechRecognition, ev: Event) => any;
  onspeechend: (this: SpeechRecognition, ev: Event) => any;
  onspeechstart: (this: SpeechRecognition, ev: Event) => any;
  onstart: (this: SpeechRecognition, ev: Event) => any;
  abort(): void;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  var webkitSpeechRecognition: SpeechRecognitionConstructor;
  var SpeechRecognition: SpeechRecognitionConstructor;
}

/**
 * A simple, reliable hook for voice input using the Web Speech API
 * This replaces the complex Whisper implementation with native browser speech recognition
 */
export function useVoiceInput(options: UseVoiceInputOptions = {}): UseVoiceInputReturn {
  const {
    onResult,
    onInterimResult,
    onError,
    language = "en-US",
    continuous = false
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const onResultRef = useRef(onResult);
  const onInterimResultRef = useRef(onInterimResult);
  const onErrorRef = useRef(onError);
  const lastProcessedLengthRef = useRef(0); // Track last processed final transcript length

  // Keep refs updated
  useEffect(() => {
    onResultRef.current = onResult;
    onInterimResultRef.current = onInterimResult;
    onErrorRef.current = onError;
  }, [onResult, onInterimResult, onError]);

  // Check if Web Speech API is supported
  const isSupported = typeof window !== "undefined" &&
    ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  const setupRecognition = useCallback(() => {
    if (!isSupported) return;

    // Create recognition instance
    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    const recognition = new SpeechRecognition();

    // Configure recognition
    recognition.continuous = continuous;
    recognition.interimResults = true; // Enable interim results for live transcription
    recognition.lang = language;
    recognition.maxAlternatives = 1;

    // Event handlers
    recognition.onstart = () => {
      // Reset tracking and state when starting new recognition
      lastProcessedLengthRef.current = 0;
      setTranscript(""); // Clear previous transcript
      setInterimTranscript(""); // Clear previous interim
      setIsListening(true);
      if (onInterimResultRef.current) {
        onInterimResultRef.current("Listening...");
      }
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      let currentInterimTranscript = "";

      // Process all results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcriptText = result[0]?.transcript || "";

        if (result.isFinal) {
          finalTranscript += transcriptText;
        } else {
          currentInterimTranscript += transcriptText;
        }
      }

      // Handle final transcript - only emit new content
      if (finalTranscript) {
        const currentLength = finalTranscript.length;
        const previouslyProcessedLength = lastProcessedLengthRef.current;
        const newText = finalTranscript.slice(previouslyProcessedLength);

        // Only emit and accumulate if there's new content
        if (newText) {
          setTranscript((prev: string) => prev + newText);
          if (onResultRef.current) {
            onResultRef.current(newText);
          }
          lastProcessedLengthRef.current = currentLength;
        }
      }

      // Handle interim transcript - always shows current live speech
      if (currentInterimTranscript) {
        setInterimTranscript(currentInterimTranscript);
        if (onInterimResultRef.current) {
          onInterimResultRef.current(currentInterimTranscript);
        }
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      // Clear interim transcript when done
      setInterimTranscript("");
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      setIsListening(false);
      setInterimTranscript("");

      let errorMessage = "Speech recognition error";
      switch (event.error) {
        case 'network':
          errorMessage = "Network error - check your internet connection";
          break;
        case 'audio-capture':
          errorMessage = "Could not capture audio - check your microphone";
          break;
        case 'not-allowed':
          errorMessage = "Microphone access denied - please allow microphone access";
          break;
        case 'no-speech':
          errorMessage = "No speech detected";
          break;
        case 'aborted':
          errorMessage = "Speech recognition was aborted";
          break;
        case 'language-not-supported':
          errorMessage = "Language not supported";
          break;
        case 'service-not-allowed':
          errorMessage = "Speech recognition service not allowed";
          break;
        default:
          errorMessage = event.error || "Unknown speech recognition error";
      }

      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [isSupported, language, continuous]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      if (onErrorRef.current) {
        onErrorRef.current("Speech recognition not supported in this browser");
      }
      return;
    }

    try {
      // Stop any existing recognition
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      // Setup new recognition instance
      const recognition = setupRecognition();
      if (recognition) {
        recognition.start();
      }
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
      if (onErrorRef.current) {
        onErrorRef.current(error instanceof Error ? error.message : "Failed to start speech recognition");
      }
    }
  }, [isSupported, setupRecognition]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  }, [isListening]);

  const clearTranscript = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    lastProcessedLengthRef.current = 0; // Reset tracking
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.stop();
      }
    };
  }, [isListening]);

  return {
    isListening,
    isSupported,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    clearTranscript,
  };
}
